import React from "react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import {
  Download,
  Users,
  Clock,
  Shield,
  Smartphone,
  Laptop,
  CheckCircle2,
  Zap,
  ExternalLink,
} from "lucide-react";

interface AppData {
  id: string;
  name: string;
  description: string;
  icon: React.ReactNode;
  color: string;
  features: string[];
  downloadLinks: {
    windows?: string;
    mac?: string;
    android?: string;
    ios?: string;
  };
  version: string;
  size: string;
  lastUpdated: string;
}

export const AppDownloads: React.FC = () => {
  const apps: AppData[] = [
    {
      id: "hr-payroll",
      name: "HR Payroll",
      description:
        "Complete HR and payroll management solution. Manage employees, attendance, leaves, salaries, and generate reports.",
      icon: <Users className="h-8 w-8" />,
      color: "from-blue-500 to-indigo-600",
      features: [
        "Employee Management",
        "Attendance Tracking",
        "Leave Management",
        "Payroll Processing",
        "Tax Calculations",
        "Report Generation",
        "Salary Slips",
        "Performance Reviews",
      ],
      downloadLinks: {
        android: "https://api-hr.startupkhata.com/download_hr_app/hr_payroll",
        ios: "https://apps.apple.com/app/hr-payroll/id123456789",
      },
      version: "3.2.1",
      size: "10 MB",
      lastUpdated: "2026-01-15",
    },
    {
      id: "work-monitor",
      name: "Work Monitor",
      description:
        "Employee activity monitoring and productivity tracking. Track applications, websites, and work efficiency in real-time.",
      icon: <Clock className="h-8 w-8" />,
      color: "from-purple-500 to-pink-600",
      features: [
        "Activity Tracking",
        "Application Usage",
        "Website Monitoring",
        "Productivity Reports",
        "Screenshot Capture",
        "Idle Time Detection",
        "Real-time Dashboard",
        "Data Export",
      ],
      downloadLinks: {
        windows: "https://api-hr.startupkhata.com/download_hr_app/work_monitor",
        mac: "https://example.com/work-monitor-mac.dmg",
      },
      version: "2.5.0",
      size: "136 MB",
      lastUpdated: "2026-03-24",
    },
  ];

  const getPlatformIcon = (platform: string) => {
    switch (platform) {
      case "windows":
        return <Laptop className="h-4 w-4" />;
      case "mac":
        return <Laptop className="h-4 w-4" />;
      case "android":
        return <Smartphone className="h-4 w-4" />;
      case "ios":
        return <Smartphone className="h-4 w-4" />;
      default:
        return <Download className="h-4 w-4" />;
    }
  };

  const getPlatformName = (platform: string) => {
    switch (platform) {
      case "windows":
        return "Windows";
      case "mac":
        return "macOS";
      case "android":
        return "Android";
      case "ios":
        return "iOS";
      default:
        return platform;
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-slate-50">
      {/* Header */}
      <div className="bg-white border-b border-slate-200 sticky top-0 z-20 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-gradient-to-r from-blue-500 to-purple-600 rounded-xl">
              <Download className="h-5 w-5 text-white" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-slate-800">
                App Downloads
              </h1>
              <p className="text-sm text-slate-500">
                Download HR Payroll and Work Monitor applications
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Apps Grid */}
        <div className="grid lg:grid-cols-2 gap-6 mb-8">
          {apps.map((app) => (
            <Card
              key={app.id}
              className="border-0 shadow-lg hover:shadow-xl transition-all duration-300 overflow-hidden"
            >
              <div className={`bg-gradient-to-r ${app.color} p-6 text-white`}>
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-white/20 rounded-xl backdrop-blur">
                      {app.icon}
                    </div>
                    <div>
                      <h2 className="text-2xl font-bold">{app.name}</h2>
                      <p className="text-sm text-white/80 mt-1">
                        Version {app.version} • {app.size}
                      </p>
                    </div>
                  </div>
                  <Badge className="bg-white/20 text-white border-0">
                    Updated{" "}
                    {new Date(app.lastUpdated).toLocaleDateString("en-GB")}
                  </Badge>
                </div>
              </div>

              <CardContent className="p-6">
                <p className="text-slate-600 mb-6">{app.description}</p>

                {/* Features */}
                <div className="mb-6">
                  <h3 className="text-sm font-semibold text-slate-700 mb-3 flex items-center gap-2">
                    <CheckCircle2 className="h-4 w-4 text-green-500" />
                    Key Features
                  </h3>
                  <div className="grid grid-cols-2 gap-2">
                    {app.features.map((feature, idx) => (
                      <div
                        key={idx}
                        className="flex items-center gap-2 text-xs text-slate-600"
                      >
                        <Zap className="h-3 w-3 text-blue-500" />
                        <span>{feature}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Download Links */}
                <div className="space-y-3">
                  <h3 className="text-sm font-semibold text-slate-700 mb-3 flex items-center gap-2">
                    <Download className="h-4 w-4 text-blue-500" />
                    Download Options
                  </h3>

                  <div className="grid grid-cols-2 gap-3">
                    {Object.entries(app.downloadLinks).map(
                      ([platform, link]) => (
                        <a
                          key={platform}
                          href={link}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center justify-between p-2.5 border border-slate-200 rounded-lg hover:border-blue-300 hover:shadow-md transition-all bg-white text-slate-700 hover:text-blue-600"
                        >
                          <span className="flex items-center gap-2">
                            {getPlatformIcon(platform)}
                            <span className="text-sm font-medium">
                              {getPlatformName(platform)}
                            </span>
                          </span>
                          <ExternalLink className="h-3 w-3 opacity-50" />
                        </a>
                      ),
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* System Requirements */}
        <Card className="border-0 shadow-lg bg-gradient-to-r from-slate-50 to-white">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Shield className="h-5 w-5 text-blue-500" />
              System Requirements
            </CardTitle>
            <CardDescription>
              Make sure your system meets these requirements for optimal
              performance
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <h4 className="font-semibold text-sm mb-2 flex items-center gap-2">
                  <Laptop className="h-4 w-4" />
                  Desktop
                </h4>
                <ul className="space-y-1 text-sm text-slate-600">
                  <li>• Windows 10/11 or macOS 11+</li>
                  <li>• 4GB RAM minimum (8GB recommended)</li>
                  <li>• 500MB free disk space</li>
                  <li>• Intel Core i3 or equivalent</li>
                  <li>• Internet connection for sync</li>
                </ul>
              </div>
              <div>
                <h4 className="font-semibold text-sm mb-2 flex items-center gap-2">
                  <Smartphone className="h-4 w-4" />
                  Mobile
                </h4>
                <ul className="space-y-1 text-sm text-slate-600">
                  <li>• Android 10+ or iOS 14+</li>
                  <li>• 2GB RAM minimum</li>
                  <li>• 100MB free storage</li>
                  <li>• Stable internet connection</li>
                </ul>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Support Section */}
        <div className="mt-6 text-center">
          <p className="text-sm text-slate-500">
            Need help? Contact our support team at{" "}
            <a
              href="mailto:support@startupkhata.com"
              className="text-blue-600 hover:underline"
            >
              support@startupkhata.com
            </a>{" "}
            or call (011) 404-52-748
          </p>
        </div>
      </div>
    </div>
  );
};

export default AppDownloads;
